import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DFCCard } from './components/DFCCard';
import { GraficoDFC } from './components/GraficoDFC';
import { TabelaDRE } from './components/TabelaDRE';
import { AlertasBadge } from './components/AlertasBadge';
import { CompanySelector } from './components/CompanySelector';
import { dfcApi } from './api/client';
import { useQuery } from '@tanstack/react-query';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60 * 1000, retry: 1 } },
});

interface DashboardProps {
  empresa_id: string;
  periodo_inicio: string;
  periodo_fim: string;
}

function Dashboard({ empresa_id, periodo_inicio, periodo_fim }: DashboardProps) {
  const { data: dfc } = useQuery({
    queryKey: ['dfc', empresa_id, periodo_inicio, periodo_fim],
    queryFn: () => dfcApi.calcular(empresa_id, periodo_inicio, periodo_fim).then((r) => r.data),
    enabled: !!empresa_id,
  });

  const dreLinhas: any[] = [];

  return (
    <div style={styles.body}>
      <div style={styles.topRow}>
        <DFCCard empresa_id={empresa_id} periodo_inicio={periodo_inicio} periodo_fim={periodo_fim} />
      </div>

      {dfc && (
        <div style={styles.midRow}>
          <GraficoDFC consolidado={dfc.consolidado} periodos={dfc.periodos} linha="recebimento" />
          <GraficoDFC consolidado={dfc.consolidado} periodos={dfc.periodos} linha="margem_contribuicao" />
        </div>
      )}

      {dreLinhas.length > 0 && (
        <div style={styles.dreRow}>
          <TabelaDRE linhas={dreLinhas} />
        </div>
      )}
    </div>
  );
}

export function App() {
  const [empresaId, setEmpresaId] = useState<string>(() => {
    const el = document.getElementById('concilhe-dashboard-root');
    return el?.dataset.empresaId ?? localStorage.getItem('concilhe_empresa_id') ?? '';
  });

  const periodo = {
    inicio: new Date().toISOString().slice(0, 7),
    fim:    new Date().toISOString().slice(0, 7),
  };

  return (
    <QueryClientProvider client={queryClient}>
      <div style={styles.page}>
        <header style={styles.header}>
          <h1 style={styles.h1}>Concilhe — Dashboard Gerencial</h1>
          <div style={styles.headerRight}>
            <CompanySelector current={empresaId} onSelect={setEmpresaId} />
            <AlertasBadge empresa_id={empresaId} />
          </div>
        </header>

        {empresaId ? (
          <Dashboard
            empresa_id={empresaId}
            periodo_inicio={periodo.inicio}
            periodo_fim={periodo.fim}
          />
        ) : (
          <div style={styles.empty}>
            <p>Selecione uma empresa para visualizar o dashboard.</p>
          </div>
        )}
      </div>
    </QueryClientProvider>
  );
}

export default App;

const styles: Record<string, React.CSSProperties> = {
  page: {
    background: '#141722',
    minHeight: '100vh',
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  h1: { margin: 0, color: '#e2e8f0', fontSize: 20, fontWeight: 700 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 16 },
  body: { display: 'flex', flexDirection: 'column', gap: 20 },
  topRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 },
  midRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 16 },
  dreRow: {},
  empty: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    color: '#64748b',
    fontSize: 15,
  },
};
